import React, { memo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { useTasks } from 'core/hooks';
import { Colors } from 'services/ColorService';

interface ITasksPanelProps {
  studentId?: string;
  groupId?: string;
  /** Scope tasks to one agreement (договор) — used inside the AgreementCard. */
  agreementId?: string;
  /** Tighter layout (no top margin) for embedding inside another card. */
  compact?: boolean;
}

const PRIORITIES = ['low', 'normal', 'high'] as const;
const STATUSES = ['open', 'in_progress', 'done', 'cancelled'] as const;
const DONE = 'done';

/**
 * Staff task tracker panel for a student, a group OR a single agreement
 * (spec 0003 Phase 2 + redesign: student / договор / group). Add a task
 * (title / due date / priority), toggle done, delete. Shared by StudentDetail,
 * GroupDetail and the per-agreement AgreementCard.
 */
export const TasksPanel = memo(
  ({ studentId, groupId, agreementId, compact = false }: ITasksPanelProps) => {
    const { t } = useTranslation();
    const [includeClosed, setIncludeClosed] = useState(false);
    const { tasks, loading, saving, add, setStatus, remove } = useTasks({
      studentId,
      groupId,
      agreementId,
      includeClosed,
    });

    const [title, setTitle] = useState('');
    const [dueDate, setDueDate] = useState('');
    const [priority, setPriority] = useState<string>('normal');

    const onAdd = useCallback(async () => {
      if (!title.trim()) return;
      const ok = await add({
        title: title.trim(),
        dueDate: dueDate || undefined,
        priority,
      });
      if (ok) {
        setTitle('');
        setDueDate('');
        setPriority('normal');
      }
    }, [add, title, dueDate, priority]);

    const today = new Date().toISOString().slice(0, 10);

    return (
      <Card compact={compact}>
        <Header>
          <CardTitle>{t('pages.tasks.title')}</CardTitle>
          <Toggle>
            <input
              type="checkbox"
              checked={includeClosed}
              onChange={(e) => setIncludeClosed(e.target.checked)}
            />
            {t('pages.tasks.showClosed')}
          </Toggle>
        </Header>

        <Form>
          <TitleInput
            value={title}
            placeholder={t('pages.tasks.placeholder')}
            onChange={(e) => setTitle(e.target.value)}
          />
          <Row>
            <DateInput
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
            <Select
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
            >
              {PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {t(`pages.tasks.priority.${p}`)}
                </option>
              ))}
            </Select>
            <AddButton
              type="button"
              disabled={saving || !title.trim()}
              onClick={onAdd}
            >
              {t('pages.tasks.add')}
            </AddButton>
          </Row>
        </Form>

        {loading && tasks.length === 0 ? (
          <Muted>{t('pages.tasks.loading')}</Muted>
        ) : tasks.length === 0 ? (
          <Muted>{t('pages.tasks.empty')}</Muted>
        ) : (
          <List>
            {tasks.map((task) => {
              const overdue =
                task.status !== DONE &&
                task.dueDate != null &&
                task.dueDate < today;
              const isDone = task.status === DONE;
              return (
                <Item key={task.id} done={isDone}>
                  <ItemMain>
                    <ItemTitle done={isDone}>{task.title}</ItemTitle>
                    <Badges>
                      <Badge tone={isDone ? 'success' : 'neutral'}>
                        {t(`pages.tasks.status.${task.status}`)}
                      </Badge>
                      {task.priority === 'high' ? (
                        <Badge tone="warn">
                          {t('pages.tasks.priority.high')}
                        </Badge>
                      ) : null}
                      {task.dueDate ? (
                        <DueDate overdue={overdue}>
                          {t('pages.tasks.due')}: {task.dueDate}
                        </DueDate>
                      ) : null}
                      {!agreementId && task.agreement?.agreementTitle ? (
                        <Badge tone="neutral">
                          {task.agreement.agreementTitle}
                        </Badge>
                      ) : null}
                      {task.assigneeName ? (
                        <span>{task.assigneeName}</span>
                      ) : null}
                    </Badges>
                  </ItemMain>
                  <Actions>
                    <Select
                      value={task.status}
                      onChange={(e) => setStatus(task.id, e.target.value)}
                    >
                      {STATUSES.map((s) => (
                        <option key={s} value={s}>
                          {t(`pages.tasks.status.${s}`)}
                        </option>
                      ))}
                    </Select>
                    <LinkButton
                      type="button"
                      tone="danger"
                      onClick={() => remove(task.id)}
                    >
                      {t('pages.tasks.delete')}
                    </LinkButton>
                  </Actions>
                </Item>
              );
            })}
          </List>
        )}
      </Card>
    );
  },
);

const Card = styled.div<{ compact: boolean }>`
  margin-top: ${({ compact }) => (compact ? '0' : '16px')};
  padding: ${({ compact }) => (compact ? '12px 0 0' : '14px 16px')};
  border: ${({ compact }) =>
    compact ? 'none' : `1px solid ${Colors.SecondLightGray}`};
  border-radius: ${({ compact }) => (compact ? '0' : '10px')};
  background: ${({ compact }) => (compact ? 'transparent' : Colors.White)};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const CardTitle = styled.div`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${Colors.Gray};
`;

const Toggle = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: ${Colors.Gray};
  cursor: pointer;
`;

const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
`;

const TitleInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  border: 1px solid ${Colors.SecondLightGray};
  border-radius: 8px;
  padding: 8px 10px;
  font: inherit;
  font-size: 13px;
`;

const Row = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

const DateInput = styled.input`
  border: 1px solid ${Colors.SecondLightGray};
  border-radius: 8px;
  padding: 6px 8px;
  font: inherit;
  font-size: 13px;
`;

const Select = styled.select`
  border: 1px solid ${Colors.SecondLightGray};
  border-radius: 8px;
  padding: 6px 8px;
  font: inherit;
  font-size: 13px;
`;

const AddButton = styled.button`
  border: 1px solid ${Colors.Blue};
  background: ${Colors.Blue};
  color: ${Colors.White};
  border-radius: 8px;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const Muted = styled.div`
  font-size: 13px;
  color: ${Colors.Gray};
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Item = styled.div<{ done: boolean }>`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  border: 1px solid ${Colors.SecondLightGray};
  border-radius: 8px;
  padding: 8px 10px;
  opacity: ${({ done }) => (done ? 0.6 : 1)};
`;

const ItemMain = styled.div`
  min-width: 0;
`;

const ItemTitle = styled.div<{ done: boolean }>`
  font-size: 13px;
  font-weight: 600;
  color: ${Colors.Black};
  text-decoration: ${({ done }) => (done ? 'line-through' : 'none')};
`;

const Badges = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  margin-top: 4px;
  font-size: 12px;
  color: ${Colors.Gray};
`;

const Badge = styled.span<{ tone: 'success' | 'warn' | 'neutral' }>`
  font-size: 11px;
  font-weight: 700;
  border-radius: 999px;
  padding: 1px 8px;
  color: ${Colors.White};
  background: ${({ tone }) =>
    tone === 'success'
      ? Colors.SuccessDefault
      : tone === 'warn'
      ? Colors.WarningDefault
      : Colors.Gray};
`;

const DueDate = styled.span<{ overdue: boolean }>`
  color: ${({ overdue }) => (overdue ? Colors.ErrorDefault : Colors.Gray)};
  font-weight: ${({ overdue }) => (overdue ? 700 : 400)};
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  flex-shrink: 0;
`;

const LinkButton = styled.button<{ tone?: 'danger' }>`
  border: none;
  background: none;
  padding: 0;
  font: inherit;
  font-size: 12px;
  cursor: pointer;
  color: ${({ tone }) =>
    tone === 'danger' ? Colors.ErrorDefault : Colors.Blue};
`;
